import { createStudentIdentityResolver } from "./studentIdentityStore";

const MAX_IDENTIFIERS = 200;
const HINT_CLASS = "js-student-mask-search-hint";

function normalize(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function resolveIdentifiers(schoolId, query) {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) {
    return [];
  }

  const entries = createStudentIdentityResolver().getEntriesForSchool(schoolId);
  const matches = [];
  for (const [identifier, maskedName] of Object.entries(entries)) {
    if (normalize(maskedName).includes(normalizedQuery)) {
      matches.push(identifier);
      if (matches.length > MAX_IDENTIFIERS) {
        break;
      }
    }
  }
  return matches;
}

function clearHint(form) {
  const existing = form.querySelector("." + HINT_CLASS);
  if (existing) {
    existing.remove();
  }
}

function showHint(form, message, tone = "warning") {
  clearHint(form);
  const node = document.createElement("div");
  node.className = `small mt-1 w-100 ${HINT_CLASS} text-${tone}`;
  node.textContent = message;
  form.appendChild(node);
}

function setHiddenInput(form, name, value) {
  let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
  if (value === null || value === undefined || value === "") {
    if (input) {
      input.remove();
    }
    return;
  }
  if (!input) {
    input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    form.appendChild(input);
  }
  input.value = String(value);
}

function buildClearMaskUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete("q");
  url.searchParams.delete("q_identifiers");
  url.searchParams.delete("q_mask_label");
  url.searchParams.delete("q_mask_no_match");
  url.searchParams.delete("page");
  return url.pathname + (url.search ? url.search : "") + url.hash;
}

function renderMaskIndicator(form) {
  const existing = form.querySelector(".js-student-mask-indicator");
  if (existing) {
    existing.remove();
  }

  const params = new URLSearchParams(window.location.search);
  const idsRaw = params.get("q_identifiers");
  if (!idsRaw) {
    return;
  }
  const count = idsRaw.split(",").map((s) => s.trim()).filter(Boolean).length;
  if (count === 0) {
    return;
  }
  const label = (params.get("q_mask_label") || "").trim();

  const inputGroup = form.querySelector(".input-group");
  if (!inputGroup) {
    return;
  }

  const wrap = document.createElement("span");
  wrap.className = "input-group-text js-student-mask-indicator text-info-emphasis";
  wrap.title = label !== ""
    ? `Wyszukano po maskach lokalnych: „${label}” (${count} dopasowań)`
    : `Wyszukano po maskach lokalnych (${count} dopasowań)`;

  const icon = document.createElement("i");
  icon.className = "bi bi-mask me-1";
  wrap.appendChild(icon);

  const countEl = document.createElement("span");
  countEl.className = "small";
  countEl.textContent = String(count);
  wrap.appendChild(countEl);

  const clear = document.createElement("a");
  clear.href = buildClearMaskUrl();
  clear.className = "btn-close btn-close-sm ms-2";
  clear.setAttribute("aria-label", "Wyczyść filtr maski");
  clear.title = "Wyczyść filtr maski";
  wrap.appendChild(clear);

  const submitBtn = inputGroup.querySelector('button[type="submit"]');
  if (submitBtn) {
    inputGroup.insertBefore(wrap, submitBtn);
  } else {
    inputGroup.appendChild(wrap);
  }
}

function bindForm(form) {
  if (!(form instanceof HTMLFormElement) || form.dataset.maskSearchBound === "1") {
    return;
  }
  form.dataset.maskSearchBound = "1";

  const schoolId = String(form.dataset.schoolId ?? "").trim();
  if (!schoolId) {
    return;
  }

  renderMaskIndicator(form);

  form.addEventListener("form:before-serialize", (event) => {
    clearHint(form);
    setHiddenInput(form, "q_identifiers", "");
    setHiddenInput(form, "q_mask_label", "");
    setHiddenInput(form, "q_mask_no_match", "");

    const qInput = form.querySelector('input[name="q"]');
    const query = String(qInput?.value ?? "").trim();
    if (!query) {
      return;
    }

    const matches = resolveIdentifiers(schoolId, query);
    if (matches.length === 0) {
      setHiddenInput(form, "q_mask_no_match", "1");
      return;
    }

    if (matches.length > MAX_IDENTIFIERS) {
      event.preventDefault();
      showHint(form, `Zbyt wiele lokalnych dopasowań masek (>${MAX_IDENTIFIERS}). Zawęź zapytanie, aby kontynuować.`);
      return;
    }

    setHiddenInput(form, "q_identifiers", matches.join(","));
    setHiddenInput(form, "q_mask_label", query);
  });
}

export function initStudentMaskSearch(root = document) {
  const scope = root && typeof root.querySelectorAll === "function" ? root : document;
  scope.querySelectorAll('form[data-mask-search="1"]').forEach((form) => bindForm(form));
}
